import { safeLocalStorage } from '@/lib/safeStorage';
import {AUTH_EVENTS, STORAGE_KEYS} from '../constants';
import {makeApiCallWithRefresh} from './tokenRefresh';

// =====================
// Core API Configuration
// =====================

export function getApiUrl(): string {
  const url = import.meta.env.VITE_API_URL;
  if (!url) {
    throw new Error('VITE_API_URL is not defined');
  }
  return url;
}

export async function testApiConnection(): Promise<Response> {
  try {
    const res = await fetch(`${getApiUrl()}/health`, { method: 'GET' });
    console.log('Ping response:', res);
    if (!res.ok) {
      const text = await res.text();
      console.error('Ping error response body:', text);
    }
    return res;
  } catch (err) {
    console.error('Ping fetch error:', err);
    throw err;
  }
}

// Development mode helpers
export const isDevelopment = import.meta.env.DEV;
export const isLocalApi = import.meta.env.VITE_API_URL?.includes('localhost');

// =====================
// API Error
// =====================

/**
 * Error thrown for non-2xx API responses. Carries the machine-readable parts
 * of the standard error envelope `{ error: { code, message, details } }`
 * (RUT-2132) so callers can branch on specific failures via
 * `error.details.reason` / `error.code` / `error.status`, while message-only
 * consumers keep working unchanged.
 */
export class ApiError extends Error {
  status?: number;
  code?: string;
  details?: unknown;

  constructor(
    message: string,
    options?: { status?: number; code?: string; details?: unknown },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = options?.status;
    this.code = options?.code;
    this.details = options?.details;
  }
}

// =====================
// Core API Call Functions
// =====================

// Generic API call function that handles different token types and session handlers
async function makeApiCallGeneric<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  tokenGetter: () => string | null,
  sessionExpiredHandler: () => void,
  logPrefix: string,
  body?: any,
): Promise<T> {
  const token = tokenGetter();

  if (!token) {
    console.log(`[${logPrefix}] No token found in localStorage`);
    sessionExpiredHandler();
    throw new Error('No token available');
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    // Add test headers for local API server
    if (isLocalApi) {
      // Extract user info from token if possible (for better local testing)
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        headers['x-test-user-id'] = tokenPayload.sub || 'test-user-123';
        headers['x-test-org-id'] = tokenPayload['custom:orgId'] || 'ORG#test-org';
        headers['x-test-email'] = tokenPayload.email || 'test@example.com';
        headers['x-test-role'] = tokenPayload['custom:role'] || 'admin';
      } catch {
        // Fallback to default test values
        headers['x-test-user-id'] = 'test-user-123';
        headers['x-test-org-id'] = 'ORG#test-org';
        headers['x-test-email'] = 'test@example.com';
        headers['x-test-role'] = 'admin';
      }
    }

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      fetchOptions.body = JSON.stringify(body);
    }

    const url = `${getApiUrl()}${endpoint}`;
    console.log(`[${logPrefix}] ${method} ${url}`, body ? { body } : '');
    
    const res = await fetch(url, fetchOptions);

    // Check if response is successful (2xx status codes)
    if (res.status < 200 || res.status >= 300) {
      let errorMessage = `API call failed: ${method} ${endpoint}`;
      let errorCode: string | undefined;
      let errorDetails: unknown;
      try {
        const contentType = res.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const errorData = await res.json();
          // Tolerate the standard error envelope `{ error: { code, message, details } }`
          // and the legacy `{ error: "message" }` shape during the API migration.
          const envelope = errorData?.error;
          if (envelope && typeof envelope === 'object') {
            errorMessage = envelope.message || errorData?.message || errorMessage;
            errorCode = envelope.code;
            errorDetails = envelope.details;
          } else {
            errorMessage = envelope || errorData?.message || errorMessage;
          }
        } else {
          const errorText = await res.text();
          errorMessage = errorText || errorMessage;
        }
      } catch (parseError) {
        console.warn(`[${logPrefix}] Failed to parse error response:`, parseError);
        errorMessage = `HTTP ${res.status}: ${res.statusText}`;
      }
      // Surface the machine-readable code + details (e.g. error.details.reason)
      // so callers can branch on specific failures like DUPLICATE_COMPLETED_VISIT.
      throw new ApiError(errorMessage, {
        status: res.status,
        code: errorCode,
        details: errorDetails,
      });
    }

    // Handle 204 No Content responses
    if (res.status === 204) {
      console.log(`[${logPrefix}] Response: No Content (204)`);
      return undefined as T;
    }

    const responseData = await res.json();
    console.log(`[${logPrefix}] Response:`, responseData);
    return responseData;
  } catch (error: any) {
    // Handle 401 Unauthorized errors (session expired)
    if (error?.status === 401 || (error.message && (
      error.message.includes('401') ||
      error.message.includes('Unauthorized') ||
      error.message.includes('Invalid or expired') ||
      error.message.includes('Token expired')
    ))) {
      console.log(`[${logPrefix}] Session expired, prompting for re-authentication`);
      sessionExpiredHandler();
      throw new Error('Your session has expired. Please log in again.');
    }

    // Re-throw other errors as-is
    throw error;
  }
}

export async function makeApiCall<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  body?: any,
  customHeaders?: Record<string, string>,
): Promise<T> {
  return makeApiCallWithRefresh<T>(method, endpoint, body, false, customHeaders);
}

// Public API call function (no authentication required)
export async function makePublicApiCall<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  body?: any,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    fetchOptions.body = JSON.stringify(body);
  }

  const url = `${getApiUrl()}${endpoint}`;
  console.log(`[makePublicApiCall] ${method} ${url}`, body ? { body } : '');
  
  const res = await fetch(url, fetchOptions);

  // Check if response is successful (2xx status codes)
  if (res.status < 200 || res.status >= 300) {
    let errorMessage = `API call failed: ${method} ${endpoint}`;
    let errorCode: string | undefined;
    let errorDetails: unknown;
    try {
      const contentType = res.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorData = await res.json();
        // Standard envelope `{ error: { code, message, details } }` (same as makeApiCall).
        // Legacy `{ error: "message" }` still supported. Never assign the object itself —
        // that produced the "[object Object]" banner on invite onboarding.
        const envelope = errorData?.error;
        if (envelope && typeof envelope === 'object') {
          errorMessage = envelope.message || errorData?.message || errorMessage;
          errorCode = envelope.code;
          errorDetails = envelope.details;
        } else {
          errorMessage = (typeof envelope === 'string' ? envelope : undefined)
            || errorData?.message
            || errorMessage;
        }
      } else {
        const errorText = await res.text();
        errorMessage = errorText || errorMessage;
      }
    } catch (parseError) {
      console.warn(`[makePublicApiCall] Failed to parse error response:`, parseError);
      errorMessage = `HTTP ${res.status}: ${res.statusText}`;
    }
    throw new ApiError(errorMessage, {
      status: res.status,
      code: errorCode,
      details: errorDetails,
    });
  }

  const responseData = await res.json();
  console.log(`[makePublicApiCall] Response:`, responseData);
  return responseData;
}

// =====================
// Session Management
// =====================

// Global reference to the user session expired trigger function
// This will be set by the SessionExpiredProvider when it mounts
let globalUserSessionExpiredTrigger: (() => void) | null = null;

export function setUserSessionExpiredTrigger(trigger: (() => void) | null) {
  globalUserSessionExpiredTrigger = trigger;
}

// Handle session expiry by clearing tokens and showing modal
export function handleSessionExpired() {
  // Clear all stored tokens
  safeLocalStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
  safeLocalStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  safeLocalStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  // The "Viewing as" marker belongs to the session that just ended — leaving it behind stamps the
  // amber operator banner across whoever signs in next in this browser.
  safeLocalStorage.removeItem(STORAGE_KEYS.VIEW_AS);
  safeLocalStorage.setItem(STORAGE_KEYS.HAS_LOGGED_OUT, 'true');

  // Dispatch event to notify auth status hook
  window.dispatchEvent(new CustomEvent(AUTH_EVENTS.TOKENS_UPDATED));

  // Show user session expired modal using React Context
  if (globalUserSessionExpiredTrigger) {
    globalUserSessionExpiredTrigger();
  } else {
    // Fallback: direct redirect if React context isn't available
    console.warn('User session expired trigger not available, falling back to direct redirect');
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?returnTo=${encodeURIComponent(currentPath)}`;
  }
}

